import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { useState } from "react";

type Props = {
  onGoogleClick: () => void;
  onMicrosoftClick: () => void;
  onLinkedInClick: () => void;
  lastAuthMethod?: string | null;
  showApplePlaceholder?: boolean;
};

function isLastUsed(provider: "google" | "microsoft" | "linkedin" | "apple", method?: string | null) {
  if (!method) return false;
  if (provider === "google") return method === "google";
  if (provider === "microsoft") return method === "azure";
  if (provider === "linkedin") return method === "linkedin_oidc";
  if (provider === "apple") return method === "apple";
  return false;
}

export function SocialProvidersRow({
  onGoogleClick,
  onMicrosoftClick,
  onLinkedInClick,
  lastAuthMethod,
  showApplePlaceholder = true,
}: Props) {
  // 40px container with 32px glyphs
  const baseBtn =
    "h-10 w-10 p-0 rounded-[8px] border border-slate-200 bg-white text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 inline-flex items-center justify-center";
  const ringFor = (p: "google" | "microsoft" | "linkedin" | "apple") =>
    isLastUsed(p, lastAuthMethod) ? " ring-2 ring-primary ring-offset-2" : "";
  const [appleTipOpen, setAppleTipOpen] = useState(false);

  return (
    <TooltipProvider>
    <div className="mt-2 mb-10 flex items-center justify-center gap-5" data-testid="providers-row-mobile">
      <Button
        type="button"
        variant="outline"
        aria-label="Sign in with Google"
        className={`${baseBtn}${ringFor("google")}`}
        onClick={onGoogleClick}
        data-testid="google-provider"
      >
        <img
          src="/logos/partners/icons8-google.svg"
          alt=""
          className="h-7 w-7 min-h-[28px] min-w-[28px] max-h-[28px] max-w-[28px] object-contain shrink-0"
          width={28}
          height={28}
          draggable={false}
          loading="eager"
        />
      </Button>

      <Button
        type="button"
        variant="outline"
        aria-label="Sign in with Microsoft"
        className={`${baseBtn}${ringFor("microsoft")}`}
        onClick={onMicrosoftClick}
        data-testid="microsoft-provider"
      >
        <img
          src="/logos/partners/icons8-microsoft.svg"
          alt=""
          className="h-7 w-7 min-h-[28px] min-w-[28px] max-h-[28px] max-w-[28px] object-contain shrink-0"
          width={28}
          height={28}
          draggable={false}
          loading="eager"
        />
      </Button>

      <Button
        type="button"
        variant="outline"
        aria-label="Sign in with LinkedIn"
        className={`${baseBtn}${ringFor("linkedin")}`}
        onClick={onLinkedInClick}
        data-testid="linkedin-provider"
      >
        <img
          src="/logos/partners/icons8-linkedin.svg"
          alt=""
          className="h-7 w-7 min-h-[28px] min-w-[28px] max-h-[28px] max-w-[28px] object-contain shrink-0"
          width={28}
          height={28}
          draggable={false}
          loading="eager"
        />
      </Button>

      {showApplePlaceholder && (
        <Tooltip open={appleTipOpen} onOpenChange={() => {}}>
          <TooltipTrigger asChild>
            <span
              className={`${baseBtn} cursor-not-allowed border-slate-300 text-black${ringFor("apple")}`}
              tabIndex={0}
              aria-disabled="true"
              role="button"
              data-testid="apple-provider"
              onClick={() => {
                setAppleTipOpen(true);
                window.setTimeout(() => setAppleTipOpen(false), 1400);
              }}
            >
              <svg
                viewBox="0 0 50 50"
                width={26}
                height={26}
                className="h-[26px] w-[26px] min-h-[26px] min-w-[26px] max-h-[26px] max-w-[26px] object-contain shrink-0"
                aria-hidden="true"
              >
                <path fill="#808080" d="M 44.527344 34.75 C 43.449219 37.144531 42.929688 38.214844 41.542969 40.328125 C 39.601563 43.28125 36.863281 46.96875 33.480469 46.992188 C 30.46875 47.019531 29.691406 45.027344 25.601563 45.0625 C 21.515625 45.082031 20.664063 47.03125 17.648438 47 C 14.261719 46.96875 11.671875 43.648438 9.730469 40.699219 C 4.300781 32.429688 3.726563 22.734375 7.082031 17.578125 C 9.457031 13.921875 13.210938 11.773438 16.738281 11.773438 C 20.332031 11.773438 22.589844 13.746094 25.558594 13.746094 C 28.441406 13.746094 30.195313 11.769531 34.351563 11.769531 C 37.492188 11.769531 40.8125 13.480469 43.1875 16.433594 C 35.421875 20.691406 36.683594 31.78125 44.527344 34.75 Z M 31.195313 8.46875 C 32.707031 6.527344 33.855469 3.789063 33.4375 1 C 30.972656 1.167969 28.089844 2.742188 26.40625 4.78125 C 24.878906 6.640625 23.613281 9.398438 24.105469 12.066406 C 26.796875 12.152344 29.582031 10.546875 31.195313 8.46875 Z"/>
              </svg>
            </span>
          </TooltipTrigger>
          <TooltipContent>Apple (coming soon)</TooltipContent>
        </Tooltip>
      )}
    </div>
    </TooltipProvider>
  );
}
